import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  IconButton,
  Popper,
  Chip,
  Stack,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import clienteService, {
  OPTIONS_CLIENTE_OPERATIVO,
  PARAMS_CLIENTE_OPERATIVO,
  PARAMS_CLIENTE_TODOS,
} from '../../services/clienteService';
import { formatCuitDniDisplay } from '../../utils/formatters';
import {
  resolverNombreCliente,
  formatDomicilioFacturacionCliente,
  labelTipoIvaCliente,
  chipColorTipoIvaCliente,
  chipColorCondVtaCliente,
  clienteChipSx,
  readTipoIvaCliente,
} from '../../pages/facturaventa/pos/clienteNombre';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 350;

export interface ClienteInlineItem {
  nroCliente: number;
  nombre: string;
  razonSocial?: string;
  cuit?: string;
  dni?: string | number;
  direccion?: string;
  tipoIva?: string;
  condVta?: string;
  ciudad?: {
    nombreCiudad?: string | null;
    codigoPostal?: string | null;
  };
}

const toInlineItem = (c: {
  nroCliente: number;
  nombre: string;
  razonSocial?: string;
  cuit?: string;
  dni?: string | number;
  direccion?: string;
  tipoIva?: string;
  condVta?: string;
  ciudad?: {
    nombreCiudad?: string | null;
    codigoPostal?: string | null;
  };
}): ClienteInlineItem => ({
  nroCliente: c.nroCliente,
  nombre: c.nombre,
  razonSocial: c.razonSocial,
  cuit: c.cuit,
  dni: c.dni,
  direccion: c.direccion,
  tipoIva: readTipoIvaCliente(c) || undefined,
  condVta: c.condVta,
  ciudad: c.ciudad,
});

export interface ClienteInlineSearchFieldProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  excluirInactivos?: boolean;
  /** Texto cuando el cliente ya está cargado (p. ej. desde URL o cuentas-cobrar) */
  displayValue?: string;
  onSelect: (cliente: { nroCliente: number; nombre?: string }) => void;
  onOpenModal?: () => void;
  disabled?: boolean;
}

const esEntradaNumerica = (texto: string) => {
  const t = texto.trim();
  if (!t) return false;
  return t.replace(/\d/g, '') === '';
};

const terminoBusquedaApi = (trimmed: string) => {
  if (esEntradaNumerica(trimmed)) {
    return trimmed.replace(/\D/g, '');
  }
  return trimmed;
};

const debeBuscar = (trimmed: string) => trimmed.length > 0;

const mergeUnicos = (lista: ClienteInlineItem[], agregar: ClienteInlineItem[]) => {
  const out = [...lista];
  for (const c of agregar) {
    if (!out.some((x) => x.nroCliente === c.nroCliente)) out.push(c);
  }
  return out;
};

const ClienteInlineSearchField: React.FC<ClienteInlineSearchFieldProps> = ({
  label = 'Cliente',
  placeholder = 'Número, nombre o CUIT',
  helperText,
  excluirInactivos = true,
  displayValue,
  onSelect,
  onOpenModal,
  disabled = false,
}) => {
  const listParams = excluirInactivos ? PARAMS_CLIENTE_OPERATIVO : PARAMS_CLIENTE_TODOS;
  const getOptions = excluirInactivos ? OPTIONS_CLIENTE_OPERATIVO : undefined;

  const [term, setTerm] = useState(displayValue ?? '');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ClienteInlineItem[]>([]);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const userEditedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [anchorWidth, setAnchorWidth] = useState(0);

  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  useEffect(() => {
    if (!isFocused && displayValue !== undefined) {
      userEditedRef.current = false;
      setTerm((prev) => (prev === displayValue ? prev : displayValue));
      resetBusqueda();
      setLoading(false);
      searchRequestIdRef.current += 1;
    }
  }, [displayValue, isFocused, resetBusqueda]);

  const buscarPorNroExacto = useCallback(
    async (soloDigitos: string): Promise<ClienteInlineItem | null> => {
      if (soloDigitos.length === 0) return null;
      const nro = Number(soloDigitos);
      if (!Number.isFinite(nro) || nro <= 0) return null;
      try {
        const cliente = await clienteService.getCliente(nro, getOptions);
        if (!cliente) return null;
        return toInlineItem(cliente);
      } catch {
        return null;
      }
    },
    [getOptions]
  );

  const buscarClientes = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!debeBuscar(trimmed)) {
        resetBusqueda();
        setLoading(false);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setLoading(true);

      try {
        const compact = trimmed.replace(/\s+/g, '');
        if (compact !== '' && /^\d+$/.test(compact)) {
          const porNro = await buscarPorNroExacto(compact);
          if (requestId !== searchRequestIdRef.current) return;
          if (porNro) {
            setSuggestions([porNro]);
            setOpen(true);
            setHighlightedIndex(0);
            itemRefs.current = [];
          } else {
            resetBusqueda();
          }
          return;
        }

        const searchTerm = terminoBusquedaApi(trimmed);
        const res = await clienteService.getClientes({
          searchTerm,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          ...listParams,
        });

        if (requestId !== searchRequestIdRef.current) return;

        let items: ClienteInlineItem[] = (res.items ?? []).map((c) => toInlineItem(c));

        if (esEntradaNumerica(trimmed)) {
          const porNro = await buscarPorNroExacto(searchTerm);
          if (requestId !== searchRequestIdRef.current) return;
          if (porNro) items = mergeUnicos([porNro], items);
        }

        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        itemRefs.current = [];
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        resetBusqueda();
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [resetBusqueda, buscarPorNroExacto, listParams]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!isFocused || !userEditedRef.current) {
      return;
    }

    debounceRef.current = setTimeout(() => buscarClientes(term), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarClientes, isFocused]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setAnchorWidth(containerRef.current.offsetWidth);
    }
  }, [open, suggestions.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cliente: ClienteInlineItem) => {
    userEditedRef.current = false;
    onSelect(cliente);
    resetBusqueda();
    setLoading(false);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      userEditedRef.current = false;
      if (displayValue !== undefined) setTerm(displayValue);
      setLoading(false);
      return;
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const cliente = suggestions[idx];
      if (cliente) handleSelect(cliente);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (displayValue !== undefined && !open) {
      userEditedRef.current = false;
      setTerm(displayValue);
      setLoading(false);
      resetBusqueda();
    }
  };

  const resolvedHelperText =
    helperText ??
    (onOpenModal
      ? 'Escriba para buscar o use la lupa para el listado completo'
      : 'Escriba para buscar');

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        label={label || undefined}
        placeholder={placeholder}
        value={term}
        onChange={(e) => {
          userEditedRef.current = true;
          setTerm(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          if (suggestions.length > 0) setOpen(true);
        }}
        onBlur={handleBlur}
        disabled={disabled}
        helperText={resolvedHelperText || undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#ffffff',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={20} sx={{ mr: 0.5 }} />
              ) : null}
              {onOpenModal ? (
                <IconButton
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onOpenModal}
                  edge="end"
                  color="primary"
                  title="Buscar en listado completo"
                  disabled={disabled}
                  sx={{ borderRadius: 1.5 }}
                >
                  <SearchIcon />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={open && suggestions.length > 0}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        style={{ width: anchorWidth > 0 ? anchorWidth : undefined }}
      >
        <Paper
          ref={listRef}
          elevation={6}
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            borderRadius: 2,
            width: '100%',
          }}
        >
          <List dense disablePadding>
            {suggestions.map((c, idx) => {
              const nombre = resolverNombreCliente(c);
              const doc = formatCuitDniDisplay(c.cuit, c.dni, '');
              const domicilioFacturacion = formatDomicilioFacturacionCliente(c);
              const condVta = String(c.condVta ?? '').trim();
              return (
                <ListItem key={c.nroCliente} disablePadding divider={idx < suggestions.length - 1}>
                  <ListItemButton
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    selected={idx === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(c)}
                    sx={{ py: 0.85, px: 1.5 }}
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 48, fontFamily: 'monospace', flexShrink: 0 }}
                          >
                            #{c.nroCliente}
                          </Typography>
                          <Typography variant="body2" noWrap flex={1}>
                            {nombre}
                          </Typography>
                          {c.tipoIva ? (
                            <Chip
                              label={labelTipoIvaCliente(c.tipoIva)}
                              size="small"
                              color={chipColorTipoIvaCliente(c.tipoIva)}
                              sx={clienteChipSx}
                            />
                          ) : null}
                        </Box>
                      }
                      secondary={
                        doc || domicilioFacturacion || condVta ? (
                          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                            {doc || condVta ? (
                              <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.75}>
                                {doc ? (
                                  <Typography variant="caption" color="text.secondary" component="span">
                                    {doc}
                                  </Typography>
                                ) : null}
                                {condVta ? (
                                  <Chip
                                    label={condVta}
                                    size="small"
                                    color={chipColorCondVtaCliente(condVta)}
                                    sx={clienteChipSx}
                                  />
                                ) : null}
                              </Stack>
                            ) : null}
                            {domicilioFacturacion ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={{ display: 'block', lineHeight: 1.25, whiteSpace: 'normal' }}
                              >
                                {domicilioFacturacion}
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : null
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default ClienteInlineSearchField;
